import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Eye, Sparkles, FolderOpen } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import { PAGE, CARD, INPUT, LABEL, BTN_PRIMARY, FormModal } from '../../components/crm/ui';

const BLANK_TEMPLATE = { name: '', type: 'email', subject: '', content: '' };

const fillSample = (text) => text
  .replace(/\{\{leadName\}\}/g, 'John Doe')
  .replace(/\{\{companyName\}\}/g, 'Acme Corp')
  .replace(/\{\{expectedValue\}\}/g, '$12,500');

function TemplateForm({ template, onClose, onSaved }) {
  const [form, setForm] = useState(template || BLANK_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const insertPlaceholder = (tag) => set({ content: `${form.content} ${tag} ` });

  const save = async () => {
    if (!form.name || !form.content) return alertService.error('Error', 'Name and Content are required!');
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      const res = form._id ? await api.put(`/crm/templates/${form._id}`, payload) : await api.post('/crm/templates', payload);
      if (res.data.success) {
        alertService.toast('success', 'Template saved successfully!');
        onSaved();
      }
    } catch {
      alertService.error('Error', 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={form._id ? `Edit ${(form.name || '').trim()}` : 'New template'} onClose={onClose} onSubmit={save} saving={saving} submitLabel="Save template" size="max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-5">
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Template name *</label>
              <input aria-label="Template name" required className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Summer Offer Email" />
            </div>
            <div>
              <label className={LABEL}>Channel type</label>
              <select aria-label="Channel type" className={INPUT} value={form.type} onChange={(e) => set({ type: e.target.value, subject: '' })}>
                <option value="email">Email Campaign</option>
                <option value="sms">SMS Notification</option>
              </select>
            </div>
          </section>

          {form.type === 'email' && (
            <div>
              <label className={LABEL}>Email subject line *</label>
              <input aria-label="Email subject line" required className={INPUT} value={form.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="e.g. Exclusive Offer for {{leadName}}" />
            </div>
          )}

          <div>
            <label className={`${LABEL} flex items-center gap-1.5`}><Sparkles size={13} className="text-[#FF9900]" /> Lead personalization tags</label>
            <div className="flex flex-wrap gap-2">
              {[
                { tag: '{{leadName}}', label: 'Lead Name' },
                { tag: '{{companyName}}', label: 'Company' },
                { tag: '{{expectedValue}}', label: 'Deal Value' },
                { tag: '{{email}}', label: 'Email' },
              ].map((item) => (
                <button key={item.tag} type="button" onClick={() => insertPlaceholder(item.tag)} className="px-3 h-8 bg-[#F3F2F2] border border-[#D8DDE6] hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] text-[12px] font-semibold rounded transition-all whitespace-nowrap shrink-0">
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center">
              <label className={LABEL}>{form.type === 'email' ? 'HTML / rich body content *' : 'Raw SMS text content *'}</label>
              <a href="/dashboard/crm/files" target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold text-[#0176D3] hover:text-[#014486] flex items-center gap-1 mb-1">
                <FolderOpen size={12} /> Image Library
              </a>
            </div>
            <textarea
              aria-label="Template content"
              required
              value={form.content}
              onChange={(e) => set({ content: e.target.value })}
              placeholder={form.type === 'email' ? '<h1>Welcome!</h1>\n<p>Hi {{leadName}}, we are glad to connect.</p>' : 'Hi {{leadName}}, thanks for registering with us!'}
              className="w-full px-3 py-2 bg-white border border-[#D8DDE6] rounded text-[13px] font-mono h-48 outline-none focus:border-[#0176D3]"
            />
          </div>
        </div>

        <div className="lg:col-span-5 space-y-3">
          <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide flex items-center gap-1.5"><Eye size={14} className="text-[#0176D3]" /> Preview</h3>
          <div className="border border-[#D8DDE6] rounded p-4 bg-[#F4F6F9] min-h-[300px] overflow-y-auto">
            {form.type === 'email' ? (
              <div className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                <div className="bg-[#F3F2F2] p-3 border-b border-[#D8DDE6]">
                  <p className="text-[12px] text-[#706E6B]"><span className="font-bold text-[#080707]">Subject:</span> {form.subject || '(No Subject)'}</p>
                </div>
                {/* Template HTML is user-authored: render it in a fully sandboxed frame (no scripts,
                    no same-origin) so a hostile template cannot run code in a teammate's session. */}
                <iframe title="Email preview" sandbox="" srcDoc={fillSample(form.content)} className="w-full min-h-[220px] bg-white border-0" />
              </div>
            ) : (
              <div className="bg-[#EAF4FE] p-3.5 rounded-lg max-w-[280px] border border-[#B8DAF3] text-[#080707] text-[12px] shadow-sm space-y-1">
                <p className="font-bold text-[10px] text-[#0176D3] uppercase tracking-wider">SMS Message</p>
                <p className="whitespace-pre-wrap leading-relaxed">{fillSample(form.content) || '(Empty Message)'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </FormModal>
  );
}

export default function CrmTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template
  const [activeTab, setActiveTab] = useState('all'); // all, email, sms

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/crm/templates');
      if (res.data.success) setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = async (tmpl) => {
    const confirmed = await alertService.confirm('Delete Template?', `Permanently delete "${tmpl.name}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      const res = await api.delete(`/crm/templates/${tmpl._id}`);
      if (res.data.success) {
        alertService.toast('success', 'Template deleted.');
        fetchTemplates();
      }
    } catch {
      alertService.error('Error', 'Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter((t) => activeTab === 'all' || t.type === activeTab);

  return (
    <div className={PAGE}>
      <PageHeader
        title="Template Builder"
        subtitle="Design Email and SMS campaign layouts to connect with your leads."
        icon={FolderOpen}
        actions={<button type="button" onClick={() => setEditing('new')} className={BTN_PRIMARY}><Plus size={15} /> New Template</button>}
      />

      <Tabs tabs={['all', 'email', 'sms'].map((t) => ({ key: t, label: `${t.charAt(0).toUpperCase() + t.slice(1)} templates` }))} value={activeTab} onChange={setActiveTab} />

      {loading || filteredTemplates.length === 0 ? (
        <div className={`${CARD} px-4 py-12 text-center text-[#706E6B] text-[13px]`}>
          {loading ? 'Loading…' : 'No templates yet. Add your first email or SMS template.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTemplates.map((tmpl) => (
            <div key={tmpl._id} className={`${CARD} p-5 flex flex-col justify-between hover:border-[#0176D3] transition-colors`}>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`sf-badge ${tmpl.type === 'email' ? 'sf-badge-manager' : 'sf-badge-success'}`}>{tmpl.type}</span>
                  <span className="text-[10px] text-[#706E6B] font-medium">Updated {formatDate(tmpl.updatedAt)}</span>
                </div>
                <h3 className="font-semibold text-[#2E2E2E] break-words">{tmpl.name}</h3>
                {tmpl.type === 'email' && (
                  <p className="text-[12px] text-[#706E6B] line-clamp-1"><span className="font-semibold text-[#2E2E2E]">Subject:</span> {tmpl.subject}</p>
                )}
              </div>
              <div className="pt-3 border-t border-[#D8DDE6] flex justify-end gap-1 mt-5">
                <button type="button" onClick={() => setEditing(tmpl)} aria-label={`Edit ${tmpl.name}`} title={`Edit ${tmpl.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={14} /></button>
                <button type="button" onClick={() => handleDelete(tmpl)} aria-label={`Delete ${tmpl.name}`} title={`Delete ${tmpl.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateForm
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchTemplates(); }}
        />
      )}
    </div>
  );
}
